import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControlLabel, Checkbox, Button,
  IconButton, Typography, CircularProgress, useTheme, Badge,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Star as StarIcon,
  Send as SendIcon,
  Drafts as DraftsIcon,
  Delete as TrashIcon,
  Archive as ArchiveIcon,
  Schedule as SnoozedIcon,
  LabelImportant as ImportantIcon,
  Folder as FolderIcon,
  Create as ComposeIcon,
  Report as SpamIcon,
  Contacts as ContactsIcon,
  CalendarToday as CalendarIcon,
  CheckBox as TasksIcon,
  HeadsetMic as HelpdeskIcon,
  Article as DocsIcon,
  Cloud as DriveXIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Label as LabelIcon,
  Close as CloseIcon,
  MarkEmailUnread as UnreadIcon,
  AllInbox as AllMessagesIcon,
  Bookmark as FlaggedIcon,
  Inventory2 as ArchivedIcon,
  Forum as TeamChatIcon,
} from '@mui/icons-material';
import { useMailStore } from '../../stores/mailStore';
import { useComposeTabsStore } from '../../stores/composeTabsStore';
import { useThemeStore, themePalettes } from '../../stores/themeStore';

interface HybridSidebarProps {
  collapsed?: boolean;
  onItemClick?: () => void; // Callback when a folder/label is clicked (for mobile drawer close)
  isMobile?: boolean; // Whether rendering in mobile drawer mode
}

// ─── Create Folder Dialog ────────────────────────────────────────────────────
interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  customFolders: { id: string; name: string }[];
  onSave: (name: string, parentId: string, notify: boolean, imap: boolean) => Promise<void>;
}

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({ open, onClose, customFolders, onSave }) => {
  const [name, setName] = useState('');
  const [parent, setParent] = useState('/');
  const [notify, setNotify] = useState(true);
  const [imap, setImap] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setName(''); setParent('/'); setNotify(true); setImap(true); setError(''); };

  const handleClose = () => { reset(); onClose(); };

  const handleSave = async () => {
    if (!name.trim()) { setError('Folder name is required'); return; }
    setSaving(true);
    try {
      await onSave(name.trim(), parent === '/' ? '' : parent, notify, imap);
      handleClose();
    } catch (e: any) {
      setError(e.message || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '8px', p: 0 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>Create Folder</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#5f6368', mb: 0.5 }}>
            Folder Name
          </Typography>
          <TextField
            fullWidth size="small" placeholder="Folder Name"
            value={name} onChange={e => { setName(e.target.value); setError(''); }}
            error={!!error} helperText={error}
            autoFocus
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#5f6368', mb: 0.5 }}>
            Placed Under
          </Typography>
          <Select fullWidth size="small" value={parent} onChange={e => setParent(e.target.value)}
            sx={{ borderRadius: '4px', background: '#f8f9fa' }}>
            <MenuItem value="/">/</MenuItem>
            {customFolders.map(f => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel
            control={<Checkbox checked={notify} onChange={e => setNotify(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: '0.96rem' }}>Notify me of new emails</Typography>}
          />
          <FormControlLabel
            control={<Checkbox checked={imap} onChange={e => setImap(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: '0.96rem' }}>View in IMAP Client</Typography>}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ background: '#1967d2', textTransform: 'none', borderRadius: '4px', px: 2.5, '&:hover': { background: '#1557b0' } }}>
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
        <Button variant="outlined" onClick={handleClose} disabled={saving}
          sx={{ textTransform: 'none', borderRadius: '4px', px: 2.5, color: '#5f6368', borderColor: '#d0d0d0' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── HybridSidebar ───────────────────────────────────────────────────────────
const HybridSidebar: React.FC<HybridSidebarProps> = ({ collapsed = false, onItemClick, isMobile = false }) => {
  const navigate = useNavigate();
  const { folder = 'inbox' } = useParams();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { selectedPalette } = useThemeStore();
  const currentPalette = themePalettes.find(p => p.id === selectedPalette) || themePalettes[0];
  const onDarkSidebar = currentPalette.sidebarFg === 'light'; // sidebar has dark bg → needs white text
  const sidebarBg = currentPalette.sidebar;
  const composeBg = currentPalette.composeColor;
  const navTextColor = onDarkSidebar ? 'rgba(255,255,255,0.88)' : muiTheme.palette.text.primary;
  const navSecondaryColor = onDarkSidebar ? 'rgba(255,255,255,0.60)' : muiTheme.palette.text.secondary;
  const navActiveTextColor = onDarkSidebar ? '#ffffff' : muiTheme.palette.primary.main;
  const navActiveBg = onDarkSidebar ? 'rgba(255,255,255,0.15)' : '#d3e3fd';
  const navHoverBg = onDarkSidebar ? 'rgba(255,255,255,0.08)' : '#f1f3f4';
  const appLauncherBg = onDarkSidebar ? 'rgba(0,0,0,0.20)' : (isDark ? muiTheme.palette.background.default : '#fafafa');
  const sidebarDivider = onDarkSidebar ? 'rgba(255,255,255,0.12)' : muiTheme.palette.divider;
  const { folders, fetchFolders, labels, fetchLabels, createFolder } = useMailStore();
  const { createTab, setShowingCompose } = useComposeTabsStore();

  // Create folder dialog state
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  // Decode webmailFeatures from JWT — controls which app launcher icons are shown.
  // Defaults to all-visible when no JWT (e.g. during load) to avoid flicker.
  const webmailFeatures = React.useMemo(() => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.webmailFeatures as Record<string, boolean> | undefined | null;
    } catch {
      return null;
    }
  }, []);

  // Fetch chat unread count — initial load + lightweight fallback poll every 5 min.
  // Real-time updates are driven by the 'chatNewMessage' window event dispatched
  // by DomainGroupChat/TeamChatPage when a new Socket.IO message arrives.
  useEffect(() => {
    const fetchChatUnread = async () => {
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        if (!token) return;
        const apiUrl = import.meta.env.VITE_API_URL || '/api';
        const res = await fetch(`${apiUrl}/chat/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setChatUnread((data.count || 0) + (data.dmCount || 0));
        }
      } catch { /* silent */ }
    };
    fetchChatUnread();
    // Fallback poll every 5 minutes — real-time badge increments come from socket events below
    const interval = setInterval(fetchChatUnread, 300000);
    // Reset badge when user opens chat
    const handleOpen = () => setChatUnread(0);
    // Increment badge instantly when a new message arrives via Socket.IO (dispatched by DomainGroupChat)
    const handleNewMsg = () => setChatUnread(prev => prev + 1);
    window.addEventListener('openGroupChat', handleOpen);
    window.addEventListener('chatNewMessage', handleNewMsg);
    return () => {
      clearInterval(interval);
      window.removeEventListener('openGroupChat', handleOpen);
      window.removeEventListener('chatNewMessage', handleNewMsg);
    };
  }, []);

  useEffect(() => {
    fetchFolders();
    fetchLabels();
    // Keep folder counts in sync — poll every 60s.
    // Removed focus/visibilitychange triggers: they caused a full re-render
    // every time the user switched browser tabs, making the UI freeze briefly.
    const interval = setInterval(() => { fetchFolders(); }, 60000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleComposeClick = () => {
    createTab();
    onItemClick?.(); // Close mobile drawer if present
  };

  const handleFolderClick = (folderId: string) => {
    setShowingCompose(false);
    const s = useComposeTabsStore.getState();
    s.setViewingChat(false);
    s.setViewingDocs(false);
    s.setViewingSheets(false);
    navigate(`/mail/${folderId}`);
    onItemClick?.(); // Close mobile drawer if present
  };

  const handleLabelClick = (labelId: string) => {
    setShowingCompose(false);
    const s = useComposeTabsStore.getState();
    s.setViewingChat(false);
    s.setViewingDocs(false);
    s.setViewingSheets(false);
    navigate(`/mail/label-${labelId}`);
    onItemClick?.(); // Close mobile drawer if present
  };

  const isActive = (folderId: string) => folder === folderId;
  const isLabelActive = (labelId: string) => folder === `label-${labelId}`;

  const getFolderIcon = (folderId: string) => {
    switch (folderId) {
      case 'inbox':    return <InboxIcon sx={{ fontSize: 20 }} />;
      case 'starred':  return <StarIcon sx={{ fontSize: 20 }} />;
      case 'important':return <ImportantIcon sx={{ fontSize: 20 }} />;
      case 'sent':     return <SendIcon sx={{ fontSize: 20 }} />;
      case 'drafts':   return <DraftsIcon sx={{ fontSize: 20 }} />;
      case 'snoozed':  return <SnoozedIcon sx={{ fontSize: 20 }} />;
      case 'spam':     return <SpamIcon sx={{ fontSize: 20 }} />;
      case 'archive':  return <ArchiveIcon sx={{ fontSize: 20 }} />;
      case 'trash':    return <TrashIcon sx={{ fontSize: 20 }} />;
      default:         return <FolderIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getFolderIconColor = (folderId: string): string => {
    switch (folderId) {
      case 'inbox':     return '#1a73e8';
      case 'starred':   return '#f59e0b';
      case 'important': return '#f59e0b';
      case 'sent':      return '#10b981';
      case 'drafts':    return '#6366f1';
      case 'snoozed':   return '#f97316';
      case 'spam':      return '#ef4444';
      case 'archive':   return '#8b5cf6';
      case 'trash':     return '#6b7280';
      default:          return '#6b7280';
    }
  };

  const getCount = (folderId: string) => {
    const folderData = folders.find(f => f.id === folderId);
    if (!folderData) return 0;
    // Show total count for all folders so the badge reflects actual contents
    // (matches what email-list headers display, e.g. "Inbox · 8 emails").
    return folderData.totalCount || 0;
  };

  const getUnread = (folderId: string) => {
    const folderData = folders.find(f => f.id === folderId);
    if (!folderData) return 0;
    return folderData.unreadCount || 0;
  };

  // Folders that should display a count badge (per product requirement)
  const COUNT_FOLDERS = ['inbox', 'drafts', 'spam', 'trash'];

  // Get counts for special views
  const getViewCount = (viewId: string) => {
    // First, try to get the count from the folders array (API provides these)
    const viewFolder = folders.find(f => f.id === viewId);
    if (viewFolder) {
      // Unread view should show ONLY unread emails, not the total
      if (viewId === 'unread') {
        return viewFolder.unreadCount || 0;
      }
      return viewFolder.totalCount || 0;
    }
    
    // Fallback to calculated counts if not in folders array
    switch (viewId) {
      case 'unread':
        // Total unread across all folders (excluding trash/spam)
        return folders.filter(f => f.id !== 'trash' && f.id !== 'spam').reduce((sum, f) => sum + (f.unreadCount || 0), 0);
      case 'all':
        // Total messages excluding trash and spam (should come from API, but fallback if needed)
        return folders.filter(f => f.id !== 'trash' && f.id !== 'spam').reduce((sum, f) => sum + (f.totalCount || 0), 0);
      case 'flagged':
        // Count from starred folder
        return getCount('starred');
      case 'archived':
        // Count from archive folder
        return getCount('archive');
      default:
        return 0;
    }
  };

  // Custom (non-system) folders only
  const customFolders = folders.filter(f => f.type === 'custom');

  // Default labels shown when none exist in the API yet
  const DEFAULT_LABELS = [
    { id: 'work', name: 'Work', color: '#ea4335' },
    { id: 'personal', name: 'Personal', color: '#34a853' },
    { id: 'important', name: 'Important', color: '#fbbc04' },
  ];
  const displayLabels = labels.length > 0 ? labels : DEFAULT_LABELS;

  const mainFolders = [
    { id: 'inbox', label: 'Inbox' },
    { id: 'starred', label: 'Starred' },
    { id: 'snoozed', label: 'Snoozed' },
    { id: 'sent', label: 'Sent' },
    { id: 'drafts', label: 'Drafts' },
    { id: 'spam', label: 'Spam' },
    { id: 'trash', label: 'Trash' },
  ];

  const moreItems = [
    { id: 'contacts', label: 'Contacts', icon: <ContactsIcon sx={{ fontSize: 20 }} />, route: '/contacts' },
    { id: 'calendar', label: 'Calendar', icon: <CalendarIcon sx={{ fontSize: 20 }} />, route: '/calendar' },
    { id: 'tasks', label: 'Tasks', icon: <TasksIcon sx={{ fontSize: 20 }} />, route: '/tasks' },
  ];

  // Handler for app launcher clicks
  const handleAppClick = (app: typeof allAppItems[0]) => {
    if ('onClick' in app && app.onClick) {
      app.onClick();
    } else if (app.id === 'helpdesk') {
      window.open(app.route, '_blank');
    } else {
      // Navigating to a real route — close any inner-tab overlays (Docs/Sheets/Chat)
      const s = useComposeTabsStore.getState();
      s.setViewingChat(false);
      s.setViewingDocs(false);
      s.setViewingSheets(false);
      setShowingCompose(false);
      navigate(app.route);
    }
    onItemClick?.(); // Close mobile drawer
  };

  // Bottom-pinned app launchers
  const allAppItems = [
    {
      id: 'teamchat',
      label: 'Team Chat',
      sublabel: chatUnread > 0 ? `${chatUnread} unread message${chatUnread > 1 ? 's' : ''}` : 'Channels · Direct messages',
      icon: <TeamChatIcon sx={{ fontSize: 14, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
      hoverGradient: 'linear-gradient(135deg, #22d3ee 0%, #60a5fa 100%)',
      shadow: '0 4px 12px rgba(59,130,246,0.45)',
      route: '/chat',
      onClick: () => useComposeTabsStore.getState().openChat(),
    },
    {
      id: 'helpdesk',
      label: 'Helpdesk',
      sublabel: 'Support tickets',
      icon: <HelpdeskIcon sx={{ fontSize: 14, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      hoverGradient: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
      shadow: '0 4px 12px rgba(249,115,22,0.4)',
      route: '/helpdesk',
    },
    {
      id: 'docs',
      label: 'Docs & Sheets',
      sublabel: 'Documents · Spreadsheets',
      icon: <DocsIcon sx={{ fontSize: 14, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      hoverGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      shadow: '0 4px 12px rgba(5,150,105,0.4)',
      route: '/docs',
      onClick: () => useComposeTabsStore.getState().openDocs(),
    },
    {
      id: 'drivex',
      label: 'DriveX',
      sublabel: 'Cloud storage',
      icon: <DriveXIcon sx={{ fontSize: 14, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      hoverGradient: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
      shadow: '0 4px 12px rgba(99,102,241,0.4)',
      route: '/drivex',
    },
  ];

  // Filter by plan features. If webmailFeatures is null (old JWT / superadmin), show all.
  const visibleAppItems = webmailFeatures
    ? allAppItems.filter((app) => {
        if (app.id === 'teamchat')  return !!webmailFeatures.teamchat;
        if (app.id === 'helpdesk')  return !!webmailFeatures.helpdesk;
        if (app.id === 'docs')      return !!webmailFeatures.documents;
        if (app.id === 'drivex')    return !!webmailFeatures.drivex;
        return true;
      })
    : allAppItems;

  return (
    <Box
      className={`sidebar ${collapsed ? 'collapsed' : ''}`}
      sx={{
        width: isMobile ? '100%' : (collapsed ? '68px' : '260px'),
        background: sidebarBg,
        borderRight: isMobile ? 'none' : `1px solid ${sidebarDivider}`,
        color: navTextColor,
        display: 'flex',
        flexDirection: 'column',
        transition: isMobile ? 'none' : 'width 0.3s ease',
        height: '100%',
      }}
    >
      {/* Compose Button */}
      <Box className="compose-section" sx={{ padding: collapsed ? '10px 0' : '13px 12px', display: 'flex', justifyContent: 'flex-start' }}>
        <Box
          component="button"
          className="compose-btn"
          onClick={handleComposeClick}
          sx={{
            width: collapsed ? '52px' : '90%',
            height: collapsed ? '42px' : '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: collapsed ? '0' : '0 16px',
            background: composeBg,
            color: '#fff',
            border: 'none',
            borderRadius: collapsed ? '42%' : '28px',
            fontSize: '0.96rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            flexShrink: 0,
            boxShadow: '0 1px 3px 0 rgba(60,64,67,.3), 0 4px 8px 3px rgba(60,64,67,.15)',
            '&:hover': {
              boxShadow: '0 1px 4px 0 rgba(60,64,67,.3), 0 8px 12px 6px rgba(60,64,67,.15)',
            },
          }}
        >
          <ComposeIcon sx={{ fontSize: 20 }} />
          {!collapsed && <span>Compose</span>}
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box
        className="nav-section"
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 8px',
        }}
      >
        {/* Main Folders (no header) */}
        {mainFolders.map((folderItem) => {
          const count = getCount(folderItem.id);
          const active = isActive(folderItem.id);
          
          return (
            <Box
              key={folderItem.id}
              className={`nav-item ${active ? 'active' : ''}`}
              onClick={() => handleFolderClick(folderItem.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '4px 12px',
                borderRadius: collapsed ? '50%' : '0 16px 16px 0',
                cursor: 'pointer',
                color: navTextColor,
                fontSize: '0.875rem',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                transition: 'all 0.2s',
                marginBottom: '0',
                background: active ? navActiveBg : 'transparent',
                '&:hover': {
                  background: active ? navActiveBg : navHoverBg,
                },
              }}
            >
              <Box className="icon" sx={{ minWidth: '20px', display: 'flex', alignItems: 'center', color: active ? navActiveTextColor : getFolderIconColor(folderItem.id) }}>
                {getFolderIcon(folderItem.id)}
              </Box>
              {!collapsed && (
                <>
                  <Box className="label" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', color: active ? navActiveTextColor : navTextColor }}>
                    {folderItem.label}
                  </Box>
                  {COUNT_FOLDERS.includes(folderItem.id) && count > 0 && (
                    <Box
                      className="count"
                      sx={{
                        background: active ? composeBg : (onDarkSidebar ? 'rgba(255,255,255,0.15)' : (isDark ? muiTheme.palette.action.selected : '#e8eaed')),
                        color: active ? '#ffffff' : navSecondaryColor,
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '0.825rem',
                        fontWeight: 500,
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </>
              )}              
            </Box>
          );
        })}

        {/* VIEWS Section */}
        {!collapsed && (
          <>
            <Box
              className="section-header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 12px 8px 12px',
                color: navSecondaryColor,
                fontSize: '0.825rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              <Box sx={{ flex: 1 }}>VIEWS</Box>
            </Box>
            {[
              { id: 'unread',   label: 'Unread',       icon: <UnreadIcon sx={{ fontSize: 20 }} />,      route: '/mail/unread',  color: '#1a73e8' },
              { id: 'all',      label: 'All messages',  icon: <AllMessagesIcon sx={{ fontSize: 20 }} />, route: '/mail/all',     color: '#0891b2' },
              { id: 'flagged',  label: 'Flagged',       icon: <FlaggedIcon sx={{ fontSize: 20 }} />,     route: '/mail/flagged', color: '#dc2626' },
              { id: 'archived', label: 'All archived',  icon: <ArchivedIcon sx={{ fontSize: 20 }} />,    route: '/mail/archive', color: '#7c3aed' },
            ].map((view) => {
              const active = folder === view.id || (view.id === 'archived' && folder === 'archive');
              const count = getViewCount(view.id);
              return (
                <Box
                  key={view.id}
                  className={`nav-item ${active ? 'active' : ''}`}
                  onClick={() => navigate(view.route)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '4px 12px',
                    borderRadius: '0 16px 16px 0',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    letterSpacing: '-0.01em',
                    transition: 'all 0.2s',
                    background: active ? navActiveBg : 'transparent',
                    '&:hover': {
                      background: active ? navActiveBg : navHoverBg,
                    },
                  }}
                >
                  <Box sx={{ minWidth: '20px', display: 'flex', alignItems: 'center', color: active ? navActiveTextColor : view.color }}>
                    {view.icon}
                  </Box>
                  <Box sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', color: active ? navActiveTextColor : navTextColor }}>
                    {view.label}
                  </Box>
                  {view.id === 'unread' && count > 0 && (
                    <Box
                      className="count"
                      sx={{
                        background: active ? composeBg : (onDarkSidebar ? 'rgba(255,255,255,0.15)' : (isDark ? muiTheme.palette.action.selected : '#e8eaed')),
                        color: active ? '#ffffff' : navSecondaryColor,
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '0.825rem',
                        fontWeight: 500,
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/* LABELS Section */}
        {!collapsed && (
          <>
            <Box
              className="section-header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 12px 8px 12px',
                color: navSecondaryColor,
                fontSize: '0.825rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              <Box sx={{ flex: 1 }}>LABELS</Box>
              <Tooltip title="Manage labels" placement="top">
                <IconButton size="small" sx={{ p: '2px', color: navSecondaryColor }}
                  onClick={() => navigate('/settings/labels')}>
                  <SettingsIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create new label" placement="top">
                <IconButton size="small" sx={{ p: '2px', color: navSecondaryColor }}
                  onClick={() => navigate('/settings/labels?action=new')}>
                  <AddIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {displayLabels.map((label) => {
              const active = isLabelActive(label.id);
              return (
                <Box
                  key={label.id}
                  className="nav-item"
                  onClick={() => handleLabelClick(label.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '4px 12px',
                    borderRadius: '0 16px 16px 0',
                    cursor: 'pointer',
                    color: navTextColor,
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    letterSpacing: '-0.01em',
                    transition: 'all 0.2s',
                    marginBottom: '0',
                    background: active ? navActiveBg : 'transparent',
                    '&:hover': { background: active ? navActiveBg : navHoverBg },
                  }}
                >
                  <Box className="icon" sx={{ minWidth: '20px', display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '14px', height: '14px', backgroundColor: label.color, borderRadius: '2px' }} />
                  </Box>
                  <Box sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', color: active ? navActiveTextColor : navTextColor }}>
                    {label.name}
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {/* FOLDERS Section (custom folders) */}
        {!collapsed && (
          <>
            <Box
              className="section-header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 12px 8px 12px',
                color: navSecondaryColor,
                fontSize: '0.825rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              <Box sx={{ flex: 1 }}>FOLDERS</Box>
              <Tooltip title="Folder settings" placement="top">
                <IconButton size="small" sx={{ p: '2px', color: navSecondaryColor }}
                  onClick={() => navigate('/settings/folders')}>
                  <SettingsIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create a new folder" placement="top">
                <IconButton size="small" sx={{ p: '2px', color: navSecondaryColor }}
                  onClick={() => setCreateFolderOpen(true)}>
                  <AddIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {customFolders.length === 0 ? (
              <Box sx={{ px: '12px', py: '4px', color: navSecondaryColor, fontSize: '0.78rem', fontStyle: 'italic' }}>
                No folders yet
              </Box>
            ) : (
              customFolders.map((f) => {
                const active = isActive(f.id);
                const count = getCount(f.id);
                return (
                  <Box
                    key={f.id}
                    className="nav-item"
                    onClick={() => handleFolderClick(f.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '4px 12px',
                      borderRadius: '0 16px 16px 0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      letterSpacing: '-0.01em',
                      transition: 'all 0.2s',
                      marginBottom: '0',
                      background: active ? navActiveBg : 'transparent',
                      '&:hover': { background: active ? navActiveBg : navHoverBg },
                    }}
                  >
                    <Box sx={{ minWidth: '20px', display: 'flex', alignItems: 'center', color: active ? navActiveTextColor : navSecondaryColor }}>
                      <FolderIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', color: active ? navActiveTextColor : navTextColor }}>
                      {f.name}
                    </Box>
                    {count > 0 && (
                      <Box sx={{
                        background: active ? composeBg : (onDarkSidebar ? 'rgba(255,255,255,0.15)' : (isDark ? muiTheme.palette.action.selected : '#e8eaed')),
                        color: active ? '#ffffff' : navSecondaryColor,
                        padding: '2px 8px', borderRadius: '8px',
                        fontSize: '0.825rem', fontWeight: 500,
                        minWidth: '20px', textAlign: 'center',
                      }}>
                        {count}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </>
        )}

        {/* MORE Section */}
        {!collapsed && (
          <>
            <Box
              className="section-header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 12px 8px 12px',
                color: navSecondaryColor,
                fontSize: '0.825rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              MORE
            </Box>
            {moreItems.map((item) => (
              <Box
                key={item.id}
                className="nav-item"
                onClick={() => {
                  setShowingCompose(false);
                  navigate(item.route);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '4px 12px',
                  borderRadius: '0 16px 16px 0',
                  cursor: 'pointer',
                  color: navTextColor,
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  transition: 'all 0.2s',
                  marginBottom: '0',
                  '&:hover': { background: navHoverBg },
                }}
              >
                <Box className="icon" sx={{ minWidth: '20px', display: 'flex', alignItems: 'center', color: navSecondaryColor }}>
                  {item.icon}
                </Box>
                <Box className="label" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {item.label}
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        customFolders={customFolders.map(f => ({ id: f.id, name: f.name }))}
        onSave={async (name, parentId, _notify, _imap) => {
          await createFolder(name, parentId || undefined);
        }}
      />

      {/* ─── Pinned App Launcher Bar ─── */}
      <Box
        sx={{
          borderTop: `1px solid ${sidebarDivider}`,
          padding: collapsed ? '8px 6px' : '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          background: appLauncherBg,
        }}
      >
        {collapsed
          ? /* Collapsed: vertical stack of icon buttons */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
              {visibleAppItems.map((app) => (
                <Tooltip key={app.id} title={app.label} placement="right" arrow>
                  <Box
                    onClick={() => handleAppClick(app)}
                    sx={{
                      width: '29px',
                      height: '29px',
                      borderRadius: '8px',
                      background: app.gradient,
                      boxShadow: app.shadow,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      position: 'relative',
                      /* subtle top-highlight for glassy depth */
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '8px',
                        background: 'linear-gradient(160deg, rgba(255,255,255,0.22) 0%, transparent 60%)',
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        background: app.hoverGradient,
                        transform: 'translateX(3px) scale(1.12)',
                        boxShadow: app.shadow.replace('0.4', '0.7').replace('0.45', '0.75'),
                      },
                    }}
                  >
                    <Box sx={{ '& .MuiSvgIcon-root': { fontSize: '14px !important', display: 'block' } }}>
                      {app.icon}
                    </Box>
                    {app.id === 'teamchat' && chatUnread > 0 && (
                      <Box sx={{
                        position: 'absolute', top: -4, right: -4,
                        minWidth: 16, height: 16, borderRadius: '8px',
                        bgcolor: '#ef4444', color: '#fff',
                        fontSize: '0.6rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        px: '3px', border: '2px solid',
                        borderColor: isDark ? muiTheme.palette.background.paper : '#fafafa',
                        lineHeight: 1,
                      }}>
                        {chatUnread > 99 ? '99+' : chatUnread}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          : /* Expanded: horizontal icon row with generous spacing */
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'flex-start' }}>
              {visibleAppItems.map((app) => (
                <Tooltip key={app.id} title={app.label} placement="top" arrow>
                  <Box
                    onClick={() => handleAppClick(app)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '10px',
                      background: app.gradient,
                      boxShadow: app.shadow,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      position: 'relative',
                      '&:hover': {
                        background: app.hoverGradient,
                        transform: 'translateY(-3px) scale(1.12)',
                        boxShadow: app.shadow.replace('0.4', '0.7').replace('0.45', '0.75'),
                      },
                    }}
                  >
                    <Box sx={{ '& .MuiSvgIcon-root': { fontSize: '15px !important', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' } }}>
                      {app.icon}
                    </Box>
                    {app.id === 'teamchat' && chatUnread > 0 && (
                      <Box sx={{
                        position: 'absolute', top: -4, right: -4,
                        minWidth: 14, height: 14, borderRadius: '7px',
                        bgcolor: '#ef4444', color: '#fff',
                        fontSize: '0.55rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        px: '2px', border: '2px solid',
                        borderColor: isDark ? muiTheme.palette.background.paper : '#fafafa',
                        lineHeight: 1,
                      }}>
                        {chatUnread > 99 ? '99+' : chatUnread}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>}
      </Box>
    </Box>
  );
};

export default HybridSidebar;
